import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Package, MapPin, Zap, ShieldCheck } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { LoginForm } from "@/components/account/LoginForm";
import { RegisterForm } from "@/components/account/RegisterForm";
import { GoogleButton, OrDivider } from "@/components/account/GoogleButton";

const BENEFITS = [
  { icon: Zap, title: "Reserva en segundos", text: "Tus datos y dirección se rellenan solos en cada pedido." },
  { icon: Package, title: "Historial de pedidos", text: "Consulta el estado de tu ropa sin buscar el número de pedido." },
  { icon: MapPin, title: "Dirección guardada", text: "Guarda tu dirección habitual y calculamos la gasolina al momento." },
  { icon: ShieldCheck, title: "Acceso seguro", text: "Entra con tu correo y contraseña o con tu cuenta de Google." },
];

export default function CustomerLogin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/cuenta/panel";
  const defaultTab = params.get("registro") !== null ? "registro" : "entrar";

  useEffect(() => {
    if (user) navigate(next, { replace: true });
  }, [user, navigate, next]);

  const onSuccess = () => navigate(next, { replace: true });

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="customer-login-page">
      <Navbar />
      <main className="pt-24 sm:pt-32 pb-12 sm:pb-20 max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
        <div className="lg:col-span-6 fade-up lg:pt-6 order-2 lg:order-1">
          <span className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-800 text-sm font-medium px-4 py-1.5 rounded-full mb-5 sm:mb-6">
            Clientes habituales
          </span>
          <h1 className="text-3xl sm:text-5xl font-heading font-extrabold text-slate-900 tracking-tight leading-tight">
            Tu cuenta en <span className="text-cyan-600">Lavandería Ozono</span>
          </h1>
          <p className="mt-4 sm:mt-5 text-slate-600 max-w-lg">
            Guarda tus datos, repite pedidos en un par de clics y sigue tu ropa desde un solo sitio.
          </p>
          <ul className="mt-6 sm:mt-8 grid sm:grid-cols-2 gap-4 sm:gap-5">
            {BENEFITS.map((b) => (
              <li key={b.title} className="flex gap-3">
                <span className="h-10 w-10 rounded-xl bg-white border border-slate-200 text-cyan-600 flex items-center justify-center shrink-0 shadow-sm">
                  <b.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="font-heading font-semibold text-slate-900 text-sm">{b.title}</p>
                  <p className="text-sm text-slate-500 mt-0.5">{b.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-5 lg:col-start-8 fade-up order-1 lg:order-2" style={{ animationDelay: "0.1s" }}>
          <div className="bg-white border border-slate-200 rounded-3xl p-5 sm:p-9 shadow-xl shadow-slate-900/5">
            <Tabs defaultValue={defaultTab}>
              <TabsList className="w-full grid grid-cols-2 bg-slate-100 rounded-full p-1 h-auto mb-6">
                <TabsTrigger value="entrar" data-testid="auth-tab-login" className="rounded-full py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  Iniciar sesión
                </TabsTrigger>
                <TabsTrigger value="registro" data-testid="auth-tab-register" className="rounded-full py-2 data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  Crear cuenta
                </TabsTrigger>
              </TabsList>
              <TabsContent value="entrar">
                <h2 className="text-base md:text-lg font-heading font-semibold text-slate-900">Accede a tu cuenta</h2>
                <p className="text-sm text-slate-500 mt-1 mb-5">Con tu correo y contraseña, o con un clic con Google.</p>
                <LoginForm onSuccess={onSuccess} />
                <OrDivider />
                <GoogleButton next={next} />
              </TabsContent>
              <TabsContent value="registro">
                <h2 className="text-base md:text-lg font-heading font-semibold text-slate-900">Crea tu cuenta gratis</h2>
                <p className="text-sm text-slate-500 mt-1 mb-5">Un minuto y listo. También puedes registrarte con Google.</p>
                <RegisterForm onSuccess={onSuccess} />
                <OrDivider />
                <GoogleButton next={next} label="Registrarme con Google" />
              </TabsContent>
            </Tabs>
            <div className="mt-7 pt-5 border-t border-slate-100 text-xs text-slate-400 flex justify-between">
              <Link to="/seguimiento" className="hover:text-cyan-700">Seguir pedido sin cuenta</Link>
              <Link to="/admin" data-testid="customer-login-admin-link" className="hover:text-cyan-700">Acceso del equipo</Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
